<script>
  import { get } from 'svelte/store'
  import { bets, paperData, paperDay, paperCalendar, horizonFilter } from '../../stores.js'
  import { link } from '../../lib/historyNav.js'
  import { fmtWinRate } from '../../lib/format.js'
  import { formatPnlPct, mskToday } from '../../lib/tradingDay.js'
  import { refreshCore } from '../../lib/polling.js'
  import { getPaper, BETS_PAGE } from '../../api.js'
  import BetFeed from '../../components/BetFeed.svelte'
  import DayPicker from '../../components/DayPicker.svelte'
  import BalanceChart from '../../components/BalanceChart.svelte'
  import CoinTradingBar from '../../components/CoinTradingBar.svelte'

  /** Single coherent paint: pairs + feed from the same paperData snapshot. */
  $: ready = !!$paperData
  $: paperPairs = Array.isArray($paperData && $paperData.paper_pairs)
    ? $paperData.paper_pairs
    : []
  $: recent = (
    ($paperData && $paperData.feed && $paperData.feed.bets) ||
    $bets ||
    []
  ).slice(0, 8)

  $: cal = $paperCalendar || ($paperData && $paperData.calendar) || null
  $: calDays = (cal && cal.days) || {}
  $: todayId = (cal && cal.today) || ($paperData && $paperData.today) || mskToday()
  $: selectedDay = $paperDay || ($paperData && $paperData.day) || todayId
  $: daySummary = ($paperData && $paperData.day_summary) || null
  $: dayPctLabel = daySummary && daySummary.pnl_pct != null ? formatPnlPct(daySummary.pnl_pct) : ''

  let dayBusy = false
  let chartAsset = 'btc'
  let chartHorizon = '15m'

  function selectBrain(p) {
    if (!p || p.chartable === false) return
    chartAsset = String(p.asset || 'btc').toLowerCase()
    chartHorizon = String(p.horizon || '15m')
  }

  async function onDayChange(e) {
    const day = e.detail && e.detail.day
    if (!day || day === get(paperDay)) return
    paperDay.set(day)
    dayBusy = true
    try {
      await refreshCore({ resetBets: true })
    } finally {
      dayBusy = false
    }
  }

  async function onMonthChange(e) {
    const month = e.detail && e.detail.month
    if (!month) return
    dayBusy = true
    try {
      const { res, data } = await getPaper({
        betsOffset: 0,
        betsLimit: BETS_PAGE,
        horizon: get(horizonFilter) || 'all',
        asset: 'all',
        day: get(paperDay) || selectedDay,
        month,
      })
      if (res?.ok && data?.calendar) paperCalendar.set(data.calendar)
    } finally {
      dayBusy = false
    }
  }

  function balTxt(p) {
    if (p == null || p.balance == null || !Number.isFinite(Number(p.balance))) return '—'
    return Number(p.balance).toFixed(2)
  }

  /** Green / red contour vs day bank (B_day); null = flat / unknown. */
  function dayTone(p) {
    if (!p) return null
    const raw = p.day_delta
    if (raw == null || !Number.isFinite(Number(raw))) return null
    const d = Number(raw)
    if (Math.abs(d) < 0.005) return null
    return d > 0 ? 'up' : 'down'
  }
</script>

<div class="dash" aria-label="Дашборд">
  <section class="dash-zone" aria-label="Торговля по монетам">
    <CoinTradingBar mode="paper" on:changed={() => refreshCore()} />
  </section>

  <section class="dash-zone" aria-labelledby="zone-wallets">
    <header class="dash-zone__head">
      <h2 id="zone-wallets">Кошельки · % выигрышей</h2>
      <a class="dash-zone__more" href="/dashboard/brain" use:link>Мозг →</a>
    </header>
    {#if !ready}
      <p class="pm-empty">Загрузка балансов…</p>
    {:else if !paperPairs.length}
      <p class="pm-empty">Нет paper_pairs от API — проверьте dashboard / ledger paths.</p>
    {:else}
      <div class="dash-pairs">
        {#each paperPairs as p (`${p.asset}:${p.horizon}:${p.brain || 'classic'}`)}
          <button
            type="button"
            class="dash-pair"
            class:is-selected={p.chartable !== false && chartAsset === p.asset && chartHorizon === p.horizon}
            class:is-up={dayTone(p) === 'up'}
            class:is-down={dayTone(p) === 'down'}
            data-asset={p.asset}
            data-horizon={p.horizon}
            disabled={p.chartable === false}
            on:click={() => selectBrain(p)}
          >
            <p class="dash-pair__label">{p.label || `${String(p.asset).toUpperCase()} · ${p.horizon}`}</p>
            <p class="dash-pair__bal mono">
              {balTxt(p)}
              <span class="dash-pair__unit">USD</span>
            </p>
            <p class="dash-pair__sub">
              win {fmtWinRate(p.win_rate, { wins: p.wins, losses: p.losses })}
              {#if p.bet_count != null}
                · {p.bet_count} bets
              {/if}
              {#if p.wins != null && p.losses != null && Number(p.wins) + Number(p.losses) > 0}
                · {p.wins}W/{p.losses}L
              {/if}
            </p>
          </button>
        {/each}
      </div>
    {/if}
  </section>

  <section class="dash-zone">
    <BalanceChart bind:asset={chartAsset} bind:horizon={chartHorizon} day={selectedDay} />
  </section>

  <section class="dash-zone" aria-labelledby="zone-bets">
    <header class="dash-zone__head">
      <h2 id="zone-bets">Ставки</h2>
      <div class="dash-zone__tools">
        <DayPicker
          value={selectedDay}
          today={todayId}
          days={calDays}
          month={cal && cal.month}
          disabled={dayBusy}
          on:change={onDayChange}
          on:month={onMonthChange}
        />
        <a class="dash-zone__more" href="/dashboard/trades" use:link>Все →</a>
      </div>
    </header>
    <div class="dash-bets-summary">
      <p class="dash-bets-summary__bit">
        <span>день</span>
        <strong
          >{daySummary && daySummary.bet_count != null
            ? daySummary.bet_count
            : recent.length || '—'}</strong
        >
        {#if dayPctLabel}
          <em
            class="mono"
            class:is-up={daySummary && daySummary.pnl_pct > 0}
            class:is-down={daySummary && daySummary.pnl_pct < 0}>{dayPctLabel}</em
          >
        {/if}
      </p>
    </div>
    {#if !ready || dayBusy}
      <p class="pm-empty">Загрузка дня…</p>
    {:else}
      <BetFeed items={recent} showHorizon showAsset />
    {/if}
  </section>

  <section class="dash-zone" aria-labelledby="zone-brain">
    <header class="dash-zone__head">
      <h2 id="zone-brain">Мозг</h2>
    </header>
    <p class="dash-brain-hint">
      Матрица монета × TF и toggle стратегий — на странице
      <a href="/dashboard/brain" use:link>Мозг</a>.
    </p>
  </section>
</div>

<style>
  .dash {
    display: flex;
    flex-direction: column;
    gap: 1.75rem;
  }
  .dash-zone {
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
  }
  .dash-zone + .dash-zone {
    padding-top: 1.5rem;
    border-top: 1px solid var(--pm-line);
  }
  .dash-zone__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.85rem;
  }
  .dash-zone__head h2 {
    margin: 0;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
  }
  .dash-zone__more {
    font-size: 0.78rem;
    color: var(--pm-ink-soft);
    text-decoration: none;
  }
  .dash-zone__more:hover {
    color: var(--pm-ink);
  }
  .dash-zone__tools {
    display: inline-flex;
    align-items: center;
    gap: 0.65rem;
  }
  .dash-bets-summary__bit em.is-up {
    color: var(--pm-ok);
  }
  .dash-bets-summary__bit em.is-down {
    color: var(--pm-bad);
  }
  .dash-pairs {
    display: grid;
    gap: 0.75rem 1rem;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  @media (max-width: 960px) {
    .dash-pairs {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  @media (max-width: 520px) {
    .dash-pairs {
      grid-template-columns: 1fr;
    }
  }
  .dash-pair {
    appearance: none;
    display: block;
    width: 100%;
    text-align: left;
    font: inherit;
    cursor: pointer;
    padding: 0.65rem 0.75rem;
    border-radius: 10px;
    border: 1px solid var(--pm-line);
    background: var(--pm-elevated-bg, transparent);
    color: inherit;
    transition:
      border-color 0.18s var(--pm-ease-out, ease),
      box-shadow 0.18s var(--pm-ease-out, ease);
  }
  .dash-pair:hover {
    border-color: var(--pm-border-strong);
  }
  .dash-pair.is-selected {
    border-color: var(--pm-ink);
  }
  .dash-pair.is-up {
    border-color: color-mix(in srgb, var(--pm-ok) 48%, var(--pm-line));
    box-shadow:
      0 0 0 1px color-mix(in srgb, var(--pm-ok) 28%, transparent),
      0 0 16px -4px color-mix(in srgb, var(--pm-ok) 42%, transparent);
  }
  .dash-pair.is-down {
    border-color: color-mix(in srgb, var(--pm-bad) 48%, var(--pm-line));
    box-shadow:
      0 0 0 1px color-mix(in srgb, var(--pm-bad) 28%, transparent),
      0 0 16px -4px color-mix(in srgb, var(--pm-bad) 42%, transparent);
  }
  .dash-pair.is-selected.is-up {
    border-color: color-mix(in srgb, var(--pm-ok) 55%, var(--pm-ink));
  }
  .dash-pair.is-selected.is-down {
    border-color: color-mix(in srgb, var(--pm-bad) 55%, var(--pm-ink));
  }
  .dash-pair:disabled {
    cursor: default;
  }
  .dash-pair__label {
    margin: 0 0 0.3rem;
    font-size: 0.68rem;
    font-weight: 650;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
  }
  .dash-pair__bal {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: var(--pm-ink);
    font-variant-numeric: tabular-nums;
  }
  .dash-pair__unit {
    margin-left: 0.2rem;
    font-size: 0.55em;
    font-weight: 600;
    color: var(--pm-ink-mute);
    vertical-align: 0.2em;
  }
  .dash-pair__sub {
    margin: 0.35rem 0 0;
    font-size: 0.72rem;
    color: var(--pm-ink-mute);
    font-variant-numeric: tabular-nums;
  }
  .dash-bets-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem 1.25rem;
    margin-bottom: 0.85rem;
  }
  .dash-bets-summary__bit {
    margin: 0;
    display: inline-flex;
    align-items: baseline;
    gap: 0.4rem;
    font-size: 0.82rem;
    color: var(--pm-ink-mute);
  }
  .dash-bets-summary__bit span {
    font-family: var(--pm-mono);
    font-size: 0.68rem;
    letter-spacing: 0.08em;
  }
  .dash-bets-summary__bit strong {
    font-size: 1rem;
    font-weight: 650;
    color: var(--pm-ink);
    font-variant-numeric: tabular-nums;
  }
  .dash-bets-summary__bit em {
    font-style: normal;
    color: var(--pm-ink-mute);
  }
  .dash-brain-hint {
    margin: 0;
    font-size: 0.85rem;
    color: var(--pm-ink-mute);
  }
  .dash-brain-hint a {
    color: var(--pm-ink-soft);
  }
</style>
